import { Twitter, Youtube, Instagram, MessageCircle } from 'lucide-react';
import { SocialLink, FooterLink } from '../../types';
import styles from './Footer.module.css';

interface FooterProps {
  socialLinks?: SocialLink[];
  quickLinks?: FooterLink[];
  legalLinks?: FooterLink[];
  copyrightText?: string;
  studioName?: string;
}

const Footer = ({
  socialLinks = [
    { id: 'twitter', name: 'Twitter', url: 'https://twitter.com/cosmoshorizon', icon: 'twitter' },
    { id: 'discord', name: 'Discord', url: '#', icon: 'discord' },
    { id: 'youtube', name: 'YouTube', url: '#', icon: 'youtube' },
    { id: 'instagram', name: 'Instagram', url: '#', icon: 'instagram' }
  ],
  quickLinks = [
    { id: 'home', label: 'Home', href: '#hero' },
    { id: 'features', label: 'Features', href: '#features' },
    { id: 'trailer', label: 'Trailer', href: '#trailer' },
    { id: 'about', label: 'About', href: '#about' }
  ],
  legalLinks = [
    { id: 'privacy', label: 'Privacy Policy', href: '#' },
    { id: 'terms', label: 'Terms of Service', href: '#' },
    { id: 'support', label: 'Support', href: '#' }
  ],
  copyrightText = '© 2024 Cosmos Horizon. All rights reserved.',
  studioName = 'Nebula Studios'
}: FooterProps) => {
  const getSocialIcon = (iconName: string) => {
    switch (iconName) {
      case 'twitter':
        return Twitter;
      case 'discord':
        return MessageCircle;
      case 'youtube':
        return Youtube;
      case 'instagram':
        return Instagram;
      default:
        return Twitter;
    }
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const targetId = href.replace('#', '');
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <div className={styles.background} />

      <div className={styles.container}>
        {/* Main Footer Content */}
        <div className={styles.content}>
          {/* Studio Branding */}
          <div className={styles.branding}>
            <h3 className={styles.brandName}>{studioName}</h3>
            <p className={styles.brandDescription}>
              Creating immersive gaming experiences that push the boundaries of interactive entertainment.
            </p>
          </div>

          {/* Links Sections */}
          <div className={styles.linksGrid}>
            {/* Quick Links */}
            <div className={styles.linkSection}>
              <h4 className={styles.sectionTitle}>Explore</h4>
              <ul className={styles.linkList}>
                {quickLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.href}
                      className={styles.link}
                      onClick={(e) => handleLinkClick(e, link.href)}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div className={styles.linkSection}>
              <h4 className={styles.sectionTitle}>Legal</h4>
              <ul className={styles.linkList}>
                {legalLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.href}
                      className={styles.link}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social Links */}
            <div className={styles.linkSection}>
              <h4 className={styles.sectionTitle}>Connect</h4>
              <div className={styles.socialLinks}>
                {socialLinks.map((social) => {
                  const IconComponent = getSocialIcon(social.icon);
                  return (
                    <a
                      key={social.id}
                      href={social.url}
                      className={styles.socialLink}
                      aria-label={`Follow us on ${social.name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <IconComponent size={20} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={styles.bottom}>
          <p className={styles.copyright}>
            {copyrightText.replace('2024', currentYear.toString())}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
